import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  signal,
} from "@angular/core";
import { TranslateModule, TranslateService } from "@ngx-translate/core";

import { PracticeRunnerService } from "./practice-runner.service";
import type {
  Locale,
  ManeuverPhase,
  ManeuverStep,
} from "../core/gamification/gamification.types";

/**
 * Minimal in-game HUD shown while a Practice maneuver is active. Displays
 * the elapsed timer (vs. the maneuver's target time), the live command
 * checklist (steps light up green as their `autoCheck` fires), and a
 * Cancel button that aborts the run.
 *
 * The HUD never owns sim state — it only renders signals from
 * {@link PracticeRunnerService} and {@link ManeuverStore} (forwarded via
 * the runner).
 */
@Component({
  selector: "po-maneuver-hud",
  standalone: true,
  imports: [TranslateModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: "./maneuver-hud.component.html",
  styleUrl: "./maneuver-hud.component.css",
})
export class ManeuverHudComponent {
  private readonly runner = inject(PracticeRunnerService);
  private readonly translate = inject(TranslateService);

  protected readonly visible = this.runner.isRunning;
  protected readonly elapsedSec = this.runner.elapsedSec;
  protected readonly checklistMap = this.runner.checklist;
  protected readonly declaration = this.runner.declaration;

  protected readonly def = computed(() => this.declaration()?.def ?? null);
  protected readonly tier = computed(() => this.declaration()?.tier ?? null);
  protected readonly phases = computed(() => this.def()?.commandSequence ?? []);

  protected readonly targetTimeSec = computed(
    () => this.def()?.targetTimeSec ?? 0,
  );
  protected readonly timeLimitSec = computed(
    () => this.def()?.timeLimitSec ?? 0,
  );

  protected readonly elapsedLabel = computed(() =>
    formatMmSs(this.elapsedSec()),
  );
  protected readonly targetLabel = computed(() =>
    formatMmSs(this.targetTimeSec()),
  );

  protected readonly timeFraction = computed(() => {
    const limit = this.timeLimitSec();
    if (limit <= 0) return 0;
    return Math.min(1, this.elapsedSec() / limit);
  });

  protected readonly overTarget = computed(
    () => this.elapsedSec() > this.targetTimeSec(),
  );

  /**
   * Checklist panel collapsed by default — the timer + status badge stay
   * visible and the steps are revealed on demand to keep the HUD small.
   */
  protected readonly stepsExpanded = signal(false);

  /** Auto-checkable steps that have already fired this run. */
  protected readonly autoStepsDone = computed(() => {
    const phases = this.phases();
    const done = this.checklistMap();
    let n = 0;
    for (const p of phases) {
      for (const s of p.steps) {
        if (s.autoCheck !== null && done.has(`${p.id}.${s.id}`)) n++;
      }
    }
    return n;
  });

  /** Total auto-checkable steps in the maneuver. */
  protected readonly autoStepsTotal = computed(() => {
    let n = 0;
    for (const p of this.phases()) {
      for (const s of p.steps) if (s.autoCheck !== null) n++;
    }
    return n;
  });

  protected toggleSteps(): void {
    this.stepsExpanded.update((v) => !v);
  }

  protected readonly currentLocale = computed<Locale>(() =>
    coerceLocale(this.translate.currentLang ?? this.translate.getDefaultLang()),
  );

  protected labelOf(labels: { en: string; pl: string }): string {
    return labels[this.currentLocale()];
  }

  /**
   * Whether the auto-check for the given step has fired this run.
   * Steps without an auto-check show as "manual" and never auto-light.
   */
  protected isStepDone(phase: ManeuverPhase, step: ManeuverStep): boolean {
    if (step.autoCheck === null) return false;
    return this.checklistMap().has(`${phase.id}.${step.id}`);
  }

  protected trackPhase(_: number, phase: ManeuverPhase): string {
    return phase.id;
  }

  protected trackStep(_: number, step: ManeuverStep): string {
    return step.id;
  }

  protected cancel(): void {
    this.runner.abort();
  }
}

function formatMmSs(sec: number): string {
  const total = Math.max(0, Math.floor(sec));
  const mm = Math.floor(total / 60);
  const ss = total % 60;
  return `${mm}:${ss.toString().padStart(2, "0")}`;
}

function coerceLocale(lang: string | undefined): Locale {
  return lang === "en" ? "en" : "pl";
}
